import Button from '@atlaskit/button';
import {
  useAnalytics,
  SendDatabaseSortEventSources,
  sendSortEvent
} from '@databases/analytics';
import { type DatabaseField } from '@databases/types/database';
import { type DatabaseViewSettings } from '@databases/types/database/view';
// FIXME circular dependency
// eslint-disable-next-line workspaces/require-dependency
import { getSortIcon } from '@databases/view-settings/utils';
import React, { type FunctionComponent, useMemo, useCallback } from 'react';
import { useIntl } from 'react-intl-next';

import { messages } from './messages.ts';

export interface SortingActionProps {
  ascending?: boolean;
  isDisabled?: boolean;
  customOrder?: boolean;
  field: DatabaseField;
  onChange: (value: DatabaseViewSettings['sorts'][number]) => void;
  contentId: string;
}

const SortingAction: FunctionComponent<SortingActionProps> = ({
  ascending,
  isDisabled = false,
  customOrder,
  field,
  contentId,
  onChange
}) => {
  const client = useAnalytics();
  const { formatMessage } = useIntl();

  const handleChange = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      const sortSettings = {
        fieldId: field.id,
        ascending: !ascending,
        customOrder
      };
      onChange(sortSettings);
      sendSortEvent(
        client,
        sortSettings,
        SendDatabaseSortEventSources.FIELD_NAME,
        field.type,
        contentId
      );
    },
    [field.id, field.type, ascending, customOrder, onChange, client, contentId]
  );

  const icon = useMemo(() => getSortIcon(!!ascending), [ascending]);
  const ariaLabel = formatMessage(
    isDisabled
      ? ascending
        ? messages['sorting-action.ascending-read-only-label.non-final']
        : messages['sorting-action.descending-read-only-label.non-final']
      : ascending
        ? messages['sorting-action.sort-descending-label.non-final']
        : messages['sorting-action.sort-ascending-label.non-final'],
    { fieldName: field.name }
  );

  return (
    <Button
      aria-label={ariaLabel}
      isDisabled={isDisabled}
      spacing="compact"
      onClick={isDisabled ? undefined : handleChange}
      iconBefore={icon}
      appearance="subtle"
    />
  );
};

export default SortingAction;
